import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiSave,
  FiUser,
  FiCalendar,
  FiLayers,
  FiDollarSign,
  FiCheckCircle,
  FiAlertTriangle,
  FiChevronRight,
  FiChevronLeft,
  FiInfo,
  FiRefreshCw,
} from 'react-icons/fi';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { contratosAPI, planosAPI, recursosAPI, clientesAPI } from '../services/api';

const RENOVACAO_PADRAO = 12;

const padZero = (valor) => String(valor).padStart(2, '0');

const getHojeISO = () => {
  const agora = new Date();
  return `${agora.getFullYear()}-${padZero(agora.getMonth() + 1)}-${padZero(agora.getDate())}`;
};

const addMesesParaISO = (dataISO, meses) => {
  const mesesNumero = Number.parseInt(meses, 10);
  if (!dataISO || !Number.isFinite(mesesNumero)) {
    return '';
  }
  const [ano, mes, dia] = dataISO.split('-').map((parte) => Number.parseInt(parte, 10));
  if (!ano || !mes || !dia) {
    return '';
  }
  const data = new Date(ano, mes - 1, dia);
  data.setMonth(data.getMonth() + mesesNumero);
  return `${data.getFullYear()}-${padZero(data.getMonth() + 1)}-${padZero(data.getDate())}`;
};

const normalizarNome = (nome) => (nome || '').trim().toLowerCase();

const obterNomeCliente = (cliente) => {
  if (!cliente) return '';
  return (
    cliente.nome_fantasia ||
    cliente.nome_social ||
    cliente.razao_social ||
    cliente.nome ||
    `Cliente ${cliente.id}`
  );
};

const formatarPeriodo = (dataInicio, dataFim) => {
  if (!dataInicio) return '';
  const fim = dataFim || 'indeterminado';
  return `${dataInicio} a ${fim}`;
};

const criarCodigoIdentificador = (nomeCliente, nomePlano, dataInicio, dataFim) => {
  const limparTexto = (texto) => {
    if (!texto) return '';
    return String(texto)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase();
  };

  const clienteLimpo = limparTexto(nomeCliente);
  const planoLimpo = limparTexto(nomePlano);
  
  let periodoLimpo = '';
  if (dataInicio) {
    const dataInicioLimpa = limparTexto(dataInicio.replace(/-/g, ''));
    const dataFimLimpa = dataFim ? limparTexto(dataFim.replace(/-/g, '')) : 'indeterminado';
    periodoLimpo = `${dataInicioLimpa}${dataFimLimpa}`;
  }

  const partes = [clienteLimpo, planoLimpo, periodoLimpo].filter((p) => p.length > 0);
  return partes.join('_');
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
  max-width: 720px;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? theme.colors.primary[500] : theme.colors.text.secondary};
`;

const StepIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $active }) =>
    $active ? theme.colors.primary[100] : theme.colors.background.primary};
  border: 2px solid
    ${({ $active }) => ($active ? theme.colors.primary[500] : theme.colors.border.primary)};
  color: ${({ $active }) => ($active ? theme.colors.primary[600] : theme.colors.text.secondary)};
  font-size: ${theme.fontSizes.sm};
  font-weight: 700;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:disabled {
    background: ${theme.colors.background.muted};
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:disabled {
    background: ${theme.colors.background.muted};
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
`;

const Toggle = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${({ $active }) => ($active ? theme.colors.success[500] : theme.colors.border.primary)};
  background: ${({ $active }) => ($active ? theme.colors.success[100] : theme.colors.background.primary)};
  color: ${({ $active }) => ($active ? theme.colors.success[700] : theme.colors.text.secondary)};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.success[600]};
    color: ${theme.colors.success[700]};
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${theme.colors.primary[500]};
`;

const ReadOnlyInput = styled(Input)`
  background: ${theme.colors.background.muted};
  color: ${theme.colors.text.muted};
  cursor: not-allowed;
  font-style: italic;
  border-color: ${theme.colors.border.secondary};
  
  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const ReadOnlyLabel = styled(Label)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.muted};
`;

const FullWidthFormGroup = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${theme.spacing[3]};
`;

const ResourceButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 2px solid
    ${({ $selected }) => ($selected ? theme.colors.primary[500] : theme.colors.border.primary)};
  border-radius: ${theme.borderRadius.lg};
  background: ${({ $selected }) =>
    $selected ? theme.colors.primary[500] : theme.colors.background.primary};
  color: ${({ $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${theme.spacing[1]};
  box-shadow: ${({ $selected }) =>
    $selected ? `0 8px 16px ${theme.colors.primary[500]}40` : 'none'};

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${({ $selected }) =>
      $selected ? theme.colors.primary[600] : theme.colors.primary[50]};
    color: ${({ $selected }) =>
      $selected ? theme.colors.text.primary : theme.colors.primary[600]};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ResourceButtonTitle = styled.span`
  font-weight: 700;
  font-size: ${theme.fontSizes.sm};
  word-break: break-word;
`;

const ResourceButtonDescription = styled.span`
  font-weight: 500;
  font-size: ${theme.fontSizes.xs};
  color: ${({ $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.text.muted};
  line-height: 1.4;
  word-break: break-word;
`;

const NavigationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
`;

const StepsButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid
    ${({ $type }) =>
      $type === 'error'
        ? theme.colors.error[500]
        : $type === 'success'
        ? theme.colors.success[500]
        : theme.colors.warning[500]};
  background: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[100]
      : $type === 'success'
      ? theme.colors.success[100]
      : theme.colors.warning[100]};
  color: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[700]
      : $type === 'success'
      ? theme.colors.success[700]
      : theme.colors.warning[700]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const ResumoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${theme.spacing[4]};
`;

const ResumoCard = styled.div`
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  background: ${theme.colors.background.primary};
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ResumoTitle = styled.h4`
  margin: 0;
  font-size: ${theme.fontSizes.base};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const ResumoEntry = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const STATUS_OPTIONS = [
  { value: 'ATIVO', label: 'Ativo' },
  { value: 'CANCELADO', label: 'Cancelado' },
  { value: 'EXPIRADO', label: 'Expirado' },
];

const ContratoForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const contratoId = params.id ? Number.parseInt(params.id, 10) : null;
  const modoEdicao = Number.isFinite(contratoId);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [autoPopularRecursos, setAutoPopularRecursos] = useState(true);
  const [recursosSelecionados, setRecursosSelecionados] = useState([]);
  const [sincronizandoRecursos, setSincronizandoRecursos] = useState(false);
  const [nomesContratos, setNomesContratos] = useState([]);
  const [dataFimAutomatico, setDataFimAutomatico] = useState(!modoEdicao);
  const [form, setForm] = useState(() => {
    const dataInicioPadrao = getHojeISO();
    const renovacaoInicial = RENOVACAO_PADRAO;
    return {
      cliente_id: '',
      plano_id: '',
      nome_plano: '',
      valor_mensal: '',
      creditos_mensais: '',
      data_inicio: dataInicioPadrao,
      data_fim: addMesesParaISO(dataInicioPadrao, renovacaoInicial),
      renovacao_meses: renovacaoInicial,
      status: 'ATIVO',
      renovacao_automatica: true, // Por padrão, renovação é automática
    };
  });

  const tituloPagina = modoEdicao ? 'Editar contrato' : 'Cadastrar contrato';

  const carregarPlanos = async () => {
    const response = await planosAPI.listar({ limit: 200, page: 1, ativo: '' });
    if (response.data?.success) {
      setPlanos(response.data.data || []);
    }
  };

  const carregarRecursos = async () => {
    const response = await recursosAPI.listar({ limit: 500, page: 1 });
    if (response.data?.success) {
      setRecursos(response.data.data || []);
    }
  };

  const carregarClientes = async () => {
    try {
      const response = await clientesAPI.listar({ limit: 100, page: 1 });
      if (response.data?.success) {
        setClientes(response.data.data || []);
      }
    } catch (error) {
      console.warn('Não foi possível carregar clientes para seleção rápida.', error);
    }
  };

  const carregarNomesContratos = async () => {
    try {
      const response = await contratosAPI.listar({ limit: 500, page: 1 });
      if (response.data?.success) {
        const lista = Array.isArray(response.data.data) ? response.data.data : [];
        setNomesContratos(
          lista
            .map((contrato) => contrato?.nome_plano)
            .filter((nome) => typeof nome === 'string' && nome.trim().length > 0),
        );
      }
    } catch (error) {
      console.warn('Não foi possível carregar nomes existentes dos contratos.', error);
    }
  };

  const carregarContrato = async () => {
    if (!modoEdicao) {
      return;
    }
    try {
      const response = await contratosAPI.buscarPorId(contratoId);
      if (response.data?.success) {
        const contrato = response.data.data;
        
        // Verificar se o contrato está expirado ou cancelado - não permite edição
        if (contrato.status === 'EXPIRADO' || contrato.status === 'CANCELADO') {
          const statusTexto = contrato.status === 'EXPIRADO' ? 'expirados' : 'cancelados';
          navigate(`/gestao/contratos/${contratoId}`, { 
            state: { erro: `Não é possível editar contratos ${statusTexto}. Apenas visualização está disponível.` } 
          });
          return;
        }
        
        setForm({
          cliente_id: contrato.cliente_id || '',
          plano_id: contrato.plano_id || '',
          nome_plano: contrato.nome_plano || '',
          valor_mensal: contrato.valor_mensal || '',
          creditos_mensais: contrato.creditos_mensais || '',
          data_inicio: contrato.data_inicio ? contrato.data_inicio.substring(0, 10) : '',
          data_fim: contrato.data_fim ? contrato.data_fim.substring(0, 10) : '',
          renovacao_meses: contrato.renovacao_meses || 12,
          status: contrato.status || 'ATIVO',
          renovacao_automatica: contrato.renovacao_automatica !== undefined ? contrato.renovacao_automatica : true,
        });
        setDataFimAutomatico(false);
        setRecursosSelecionados((contrato.recursos || []).map((recurso) => recurso.id));
      } else {
        setErro('Contrato não encontrado.');
      }
    } catch (error) {
      console.error('Erro ao carregar contrato', error);
      setErro('Erro ao carregar contrato.');
    }
  };

  useEffect(() => {
    const carregarDados = async () => {
      setLoading(true);
      setErro(null);
      try {
        await Promise.all([
          carregarPlanos(),
          carregarRecursos(),
          carregarClientes(),
          carregarNomesContratos(),
          carregarContrato(),
        ]);
      } catch (error) {
        console.error('Erro ao carregar dados do contrato', error);
        setErro('Não foi possível carregar os dados necessários.');
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoId]);

  const handleChange = (campo, valor) => {
    setForm((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleClienteSelect = (valor) => {
    handleChange('cliente_id', valor);
  };

  const handleDataInicioChange = (valor) => {
    setForm((prev) => {
      const atualizado = { ...prev, data_inicio: valor };
      if (dataFimAutomatico) {
        const meses = Number.parseInt(prev.renovacao_meses, 10);
        if (valor && Number.isFinite(meses)) {
          atualizado.data_fim = addMesesParaISO(valor, meses);
        }
      }
      return atualizado;
    });
  };

  const handleRenovacaoChange = (valor) => {
    setForm((prev) => {
      const atualizado = { ...prev, renovacao_meses: valor };
      if (dataFimAutomatico && prev.data_inicio) {
        const meses = Number.parseInt(valor, 10);
        if (Number.isFinite(meses)) {
          atualizado.data_fim = addMesesParaISO(prev.data_inicio, meses);
        }
      }
      return atualizado;
    });
  };

  const handleDataFimChange = (valor) => {
    setDataFimAutomatico(false);
    handleChange('data_fim', valor);
  };

  const sincronizarRecursosDoPlano = async (planoId, mostrarErro = true) => {
    if (!planoId) return;
    try {
      setSincronizandoRecursos(true);
      const response = await planosAPI.listarRecursos(planoId);
      if (response.data?.success) {
        const ids = (response.data.data || []).map((recurso) => recurso.id);
        setRecursosSelecionados(ids);
      } else if (mostrarErro) {
        setErro('Não foi possível carregar os recursos do plano selecionado.');
      }
    } catch (error) {
      console.error('Erro ao sincronizar recursos do plano', error);
      if (mostrarErro) {
        setErro('Erro ao sincronizar recursos do plano selecionado.');
      }
    } finally {
      setSincronizandoRecursos(false);
    }
  };

  const handlePlanoChange = async (planoId) => {
    handleChange('plano_id', planoId);
    if (!planoId) {
      return;
    }
    const plano = planos.find((item) => String(item.id) === String(planoId));
    if (plano) {
      handleChange('valor_mensal', plano.valor || '');
      handleChange('creditos_mensais', plano.creditos_mensais || '');
      await sincronizarRecursosDoPlano(plano.id, false);
    }
  };

  const toggleRecurso = (recursoId) => {
    setRecursosSelecionados((prev) =>
      prev.includes(recursoId) ? prev.filter((id) => id !== recursoId) : [...prev, recursoId],
    );
  };

  const podeAvancarStep1 = useMemo(() => {
    const clienteValido = Number.isFinite(Number.parseInt(form.cliente_id, 10));
    const nomePlanoValido = form.nome_plano.trim().length > 0;
    const creditos = Number.parseInt(form.creditos_mensais, 10);
    const creditosValidos = Number.isFinite(creditos) && creditos >= 0;
    const valor = Number.parseFloat(form.valor_mensal);
    const valorValido = Number.isFinite(valor) && valor >= 0;
    const dataInicioValida = form.data_inicio && form.data_inicio.length === 10;
    const renovacao = Number.parseInt(form.renovacao_meses, 10);
    const renovacaoValida = Number.isFinite(renovacao) && renovacao > 0;
    let dataFimValida = true;
    if (form.data_fim) {
      dataFimValida = form.data_fim >= form.data_inicio;
    }
    return (
      clienteValido &&
      nomePlanoValido &&
      creditosValidos &&
      valorValido &&
      dataInicioValida &&
      renovacaoValida &&
      dataFimValida
    );
  }, [form]);

  const podeFinalizar = useMemo(() => {
    if (!podeAvancarStep1) return false;
    if (!form.status) return false;
    if (recursosSelecionados.length === 0) return false;
    return true;
  }, [podeAvancarStep1, recursosSelecionados.length, form.status]);

  const planosOrdenados = useMemo(() => {
    return [...planos].sort(
      (a, b) =>
        (Number.parseInt(a?.creditos_mensais, 10) || 0) - (Number.parseInt(b?.creditos_mensais, 10) || 0),
    );
  }, [planos]);

  useEffect(() => {
    if (form.plano_id) {
      sincronizarRecursosDoPlano(form.plano_id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.plano_id]);

  const avancar = () => setStep((prev) => Math.min(prev + 1, 3));
  const voltar = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    if (!podeFinalizar) {
      setErro('Preencha os campos obrigatórios antes de finalizar.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      setSalvando(true);

      if (modoEdicao) {
        const payload = {
          nome_plano: form.nome_plano.trim(),
          valor_mensal: Number.parseFloat(form.valor_mensal),
          creditos_mensais: Number.parseInt(form.creditos_mensais, 10),
          data_fim: form.data_fim || null,
          renovacao_meses: Number.parseInt(form.renovacao_meses, 10),
          status: form.status,
          renovacao_automatica: form.renovacao_automatica,
        };

        const response = await contratosAPI.atualizar(contratoId, payload);
        if (!response.data?.success) {
          throw new Error('Não foi possível atualizar o contrato.');
        }

        const syncResponse = await contratosAPI.sincronizarRecursos(contratoId, recursosSelecionados);
        if (!syncResponse.data?.success) {
          throw new Error('Contrato atualizado, mas ocorreu erro ao sincronizar recursos.');
        }

        setMensagem('Contrato atualizado com sucesso.');
        navigate(`/gestao/contratos/${contratoId}`);
      } else {
        const payload = {
          cliente_id: Number.parseInt(form.cliente_id, 10),
          plano_id: form.plano_id ? Number.parseInt(form.plano_id, 10) : null,
          nome_plano: form.nome_plano.trim(),
          valor_mensal: Number.parseFloat(form.valor_mensal),
          creditos_mensais: Number.parseInt(form.creditos_mensais, 10),
          data_inicio: form.data_inicio,
          data_fim: form.data_fim || null,
          renovacao_meses: Number.parseInt(form.renovacao_meses, 10),
          status: form.status,
          renovacao_automatica: form.renovacao_automatica,
          recurso_ids: recursosSelecionados,
        };

        const response = await contratosAPI.criar(payload);
        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Não foi possível criar o contrato.');
        }

        setMensagem('Contrato criado com sucesso.');
        navigate('/gestao/contratos');
      }
    } catch (error) {
      console.error('Erro ao salvar contrato', error);
      setErro(error.message || 'Erro ao salvar contrato.');
    } finally {
      setSalvando(false);
    }
  };

  const clienteSelecionado = useMemo(() => {
    const id = Number.parseInt(form.cliente_id, 10);
    return clientes.find((cliente) => Number.parseInt(cliente.id, 10) === id);
  }, [clientes, form.cliente_id]);

  const planoSelecionado = useMemo(() => {
    if (!form.plano_id) return null;
    return planos.find((plano) => String(plano.id) === String(form.plano_id)) || null;
  }, [planos, form.plano_id]);

  useEffect(() => {
    if (modoEdicao) {
      return;
    }

    if (!clienteSelecionado || !form.data_inicio) {
      return;
    }

    const nomeCliente = obterNomeCliente(clienteSelecionado);
    if (!nomeCliente) {
      return;
    }

    const nomePlanoBase = planoSelecionado?.nome || 'Planopersonalizado';
    const renovacaoMeses = Number.parseInt(form.renovacao_meses, 10);
    const dataFimAtual =
      form.data_fim || addMesesParaISO(form.data_inicio, Number.isFinite(renovacaoMeses) ? renovacaoMeses : RENOVACAO_PADRAO);
    
    const baseCodigo = criarCodigoIdentificador(nomeCliente, nomePlanoBase, form.data_inicio, dataFimAtual);

    if (!baseCodigo) {
      return;
    }

    const nomesExistentes = new Set(nomesContratos.map(normalizarNome));
    let candidato = baseCodigo;
    let candidatoNormalizado = normalizarNome(candidato);
    const nomeAtualNormalizado = normalizarNome(form.nome_plano);
    let indice = 2;

    while (nomesExistentes.has(candidatoNormalizado) && candidatoNormalizado !== nomeAtualNormalizado) {
      candidato = `${baseCodigo}_${indice}`;
      candidatoNormalizado = normalizarNome(candidato);
      indice += 1;
    }

    setForm((prev) => (prev.nome_plano === candidato ? prev : { ...prev, nome_plano: candidato }));
  }, [
    clienteSelecionado,
    planoSelecionado,
    form.data_inicio,
    form.data_fim,
    form.renovacao_meses,
    nomesContratos,
    form.nome_plano,
    modoEdicao,
  ]);

  if (loading) {
    return (
      <Layout title={tituloPagina}>
        <Container>
          <Card>
            <Subtitle>Carregando dados necessários para o contrato...</Subtitle>
          </Card>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={tituloPagina}>
      <Container>
        <Header>
          <HeaderGroup>
            <Title>{tituloPagina}</Title>
            <Subtitle>
              {modoEdicao
                ? 'Atualize os dados congelados do contrato e gerencie os recursos vinculados.'
                : 'Defina os dados congelados do contrato, recursos disponíveis e ciclo inicial.'}
            </Subtitle>
          </HeaderGroup>
          <ActionBar>
            <GhostButton onClick={() => navigate('/gestao/contratos')}>
              <FiArrowLeft />
              Voltar
            </GhostButton>
          </ActionBar>
        </Header>

        <Card>
          <Steps>
            <StepItem $active={step === 1}>
              <StepIndex $active={step === 1}>1</StepIndex>
              Dados base
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 2}>
              <StepIndex $active={step === 2}>2</StepIndex>
              Status e recursos
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 3}>
              <StepIndex $active={step === 3}>3</StepIndex>
              Revisão
            </StepItem>
          </Steps>

          {erro && (
            <Alert $type="error">
              <FiAlertTriangle />
              {erro}
            </Alert>
          )}

          {mensagem && (
            <Alert $type="success">
              <FiCheckCircle />
              {mensagem}
            </Alert>
          )}

          {step === 1 && (
            <>
              <FormGrid>
                <FormGroup>
                  <Label>Cliente *</Label>
                  <Select
                    value={
                      form.cliente_id === null || form.cliente_id === undefined || form.cliente_id === ''
                        ? ''
                        : String(form.cliente_id)
                    }
                    onChange={(e) => handleClienteSelect(e.target.value)}
                    disabled={modoEdicao}
                  >
                    <option value="">Selecionar cliente</option>
                    {clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {obterNomeCliente(cliente)}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Plano base (opcional)</Label>
                  <Select
                    value={
                      form.plano_id === null || form.plano_id === undefined || form.plano_id === ''
                        ? ''
                        : String(form.plano_id)
                    }
                    onChange={(e) => handlePlanoChange(e.target.value)}
                    disabled={modoEdicao}
                  >
                    <option value="">Selecionar plano</option>
                    {planosOrdenados.map((plano) => (
                      <option key={plano.id} value={String(plano.id)}>
                        {plano.nome} • {plano.creditos_mensais} créditos
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Valor mensal (R$) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.valor_mensal}
                    onChange={(e) => handleChange('valor_mensal', e.target.value)}
                    placeholder="Ex.: 799.90"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Créditos mensais *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.creditos_mensais}
                    onChange={(e) => handleChange('creditos_mensais', e.target.value)}
                    placeholder="Ex.: 200"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Renovação (meses) *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.renovacao_meses}
                    onChange={(e) => handleRenovacaoChange(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Data de início *</Label>
                  <Input
                    type="date"
                    value={form.data_inicio}
                    onChange={(e) => handleDataInicioChange(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Data de término</Label>
                  <Input
                    type="date"
                    value={form.data_fim || ''}
                    onChange={(e) => handleDataFimChange(e.target.value)}
                    min={form.data_inicio || undefined}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Renovação automática</Label>
                  <Select
                    value={form.renovacao_automatica ? 'true' : 'false'}
                    onChange={(e) => handleChange('renovacao_automatica', e.target.value === 'true')}
                  >
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Status do contrato</Label>
                  <Select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FullWidthFormGroup>
                  <ReadOnlyLabel>Nome do plano</ReadOnlyLabel>
                  <ReadOnlyInput
                    value={form.nome_plano}
                    readOnly
                    disabled
                    placeholder="Gerado automaticamente após selecionar o cliente"
                    aria-readonly="true"
                    title="Código identificador gerado automaticamente com base nas informações selecionadas. Este campo não pode ser editado."
                  />
                </FullWidthFormGroup>
              </FormGrid>
            </>
          )}

          {step === 2 && (
            <>
              <FormGroup>
                <Label>Recursos do contrato *</Label>
              </FormGroup>
              {recursos.length === 0 ? (
                <HelperText>
                  Nenhum recurso cadastrado. Cadastre recursos no menu "Recursos".
                </HelperText>
              ) : (
                <ResourceGrid>
                  {recursos.map((recurso) => {
                    const selecionado = recursosSelecionados.includes(recurso.id);
                    return (
                      <ResourceButton
                        key={recurso.id}
                        type="button"
                        $selected={selecionado}
                        onClick={() => toggleRecurso(recurso.id)}
                      >
                        <ResourceButtonTitle>{recurso.codigo}</ResourceButtonTitle>
                        <ResourceButtonDescription $selected={selecionado}>
                          {recurso.descricao}
                        </ResourceButtonDescription>
                      </ResourceButton>
                    );
                  })}
                </ResourceGrid>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <ResumoGrid>
                <ResumoCard>
                  <ResumoTitle>Identificação</ResumoTitle>
                  <ResumoEntry>
                    <FiUser />
                    Cliente: {clienteSelecionado ? obterNomeCliente(clienteSelecionado) : `ID ${form.cliente_id}`}
                  </ResumoEntry>
                  <ResumoEntry>
                    <FiLayers />
                    Plano: {form.nome_plano}
                  </ResumoEntry>
                  <ResumoEntry>
                    <FiDollarSign />
                    Valor mensal: R$ {Number(form.valor_mensal || 0).toFixed(2)}
                  </ResumoEntry>
                  <ResumoEntry>
                    <FiLayers />
                    Créditos mensais: {form.creditos_mensais}
                  </ResumoEntry>
                </ResumoCard>
                <ResumoCard>
                  <ResumoTitle>Vigência</ResumoTitle>
                  <ResumoEntry>
                    <FiCalendar />
                    Início: {form.data_inicio ? new Date(form.data_inicio).toLocaleDateString('pt-BR') : '—'}
                  </ResumoEntry>
                  <ResumoEntry>
                    <FiCalendar />
                    Fim: {form.data_fim ? new Date(form.data_fim).toLocaleDateString('pt-BR') : 'Sem término'}
                  </ResumoEntry>
                  <ResumoEntry>
                    <FiLayers />
                    Renovação: {form.renovacao_meses} meses
                  </ResumoEntry>
                  <ResumoEntry>
                    <FiCheckCircle />
                    Status inicial: {STATUS_OPTIONS.find((opt) => opt.value === form.status)?.label || form.status}
                  </ResumoEntry>
                </ResumoCard>
                <ResumoCard>
                  <ResumoTitle>Recursos congelados</ResumoTitle>
                  {recursosSelecionados.length > 0 ? (
                    recursos
                      .filter((recurso) => recursosSelecionados.includes(recurso.id))
                      .map((recurso) => (
                        <ResumoEntry key={recurso.id}>
                          <FiLayers /> {recurso.descricao}
                        </ResumoEntry>
                      ))
                  ) : (
                    <ResumoEntry>Nenhum recurso selecionado.</ResumoEntry>
                  )}
                </ResumoCard>
              </ResumoGrid>
            </>
          )}

          <NavigationBar>
            <StepsButtonGroup>
              {step > 1 && (
                <GhostButton type="button" onClick={voltar}>
                  <FiChevronLeft />
                  Voltar
                </GhostButton>
              )}
              {step < 3 && (
                <PrimaryButton type="button" onClick={avancar} disabled={salvando || !podeAvancarStep1}>
                  Próximo
                  <FiChevronRight />
                </PrimaryButton>
              )}
            </StepsButtonGroup>
            {step === 3 && (
              <PrimaryButton type="button" onClick={handleSubmit} disabled={salvando || !podeFinalizar}>
                <FiSave />
                {salvando ? 'Salvando...' : modoEdicao ? 'Salvar alterações' : 'Criar contrato'}
              </PrimaryButton>
            )}
          </NavigationBar>
        </Card>
      </Container>
    </Layout>
  );
};

export default ContratoForm;


